<template>
    <div class="block-analytics container-xl py-4">
        <!-- Header -->
        <div class="d-flex align-items-center justify-content-between mb-4">
            <div>
                <h2 class="text-gradient mb-1">Block {{ slot }}</h2>
                <p class="text-muted mb-0">
                    Epoch {{ block.epoch || '—' }} · {{ formatTimestamp(block.timestamp) }}
                </p>
            </div>
            <a :href="'https://solscan.io/block/' + slot" target="_blank" class="btn btn-sm btn-outline-primary">
                View on Solscan
            </a>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary"></div>
            <p class="mt-3 text-muted">Loading block data...</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <!-- Main Content -->
        <div v-show="!loading && !error">
            <!-- Hero Stats with Mini Charts -->
            <div class="analytics-hero mb-4">
                <!-- Revenue Card with Donut -->
                <div class="analytics-hero-stat">
                    <div class="hero-label">Total Revenue</div>
                    <div class="hero-value">{{ block.revenue.total.toPrecision(4) }} SOL</div>
                    <div class="hero-chart-container">
                        <canvas id="revenueDonut"></canvas>
                    </div>
                    <div class="hero-breakdown mt-2 small">
                        <div><span class="legend-dot" style="background:#6366f1"></span> Base: {{ block.revenue.baseFees.toPrecision(3) }} SOL</div>
                        <div><span class="legend-dot" style="background:#8b5cf6"></span> Priority: {{ block.revenue.priorityFees.toPrecision(3) }} SOL</div>
                        <div><span class="legend-dot" style="background:#f97316"></span> Jito: {{ block.revenue.jitoTips.toPrecision(3) }} SOL</div>
                    </div>
                </div>

                <!-- Transactions Card with Donut -->
                <div class="analytics-hero-stat hero-stat--blue">
                    <div class="hero-label">Transactions</div>
                    <div class="hero-value">{{ block.transactions.total.toLocaleString() }}</div>
                    <div class="hero-chart-container">
                        <canvas id="txDonut"></canvas>
                    </div>
                    <div class="hero-breakdown mt-2 small">
                        <div><span class="legend-dot" style="background:#3b82f6"></span> Vote: {{ block.transactions.votes.toLocaleString() }}</div>
                        <div><span class="legend-dot" style="background:#8b5cf6"></span> Non-Vote: {{ block.transactions.nonVotes.toLocaleString() }}</div>
                        <div><span class="legend-dot" style="background:#ef4444"></span> Reverted: {{ block.transactions.reverted.toLocaleString() }}</div>
                    </div>
                </div>

                <!-- CU Card with Gauge -->
                <div class="analytics-hero-stat hero-stat--green">
                    <div class="hero-label">CU Utilization</div>
                    <div class="hero-value">{{ block.compute.utilization.toFixed(1) }}%</div>
                    <div class="hero-chart-container">
                        <canvas id="cuDonut"></canvas>
                    </div>
                    <div class="hero-breakdown mt-2 small">
                        <div><span class="legend-dot" style="background:#22c55e"></span> Completed: {{ formatCU(block.compute.completed) }}</div>
                        <div><span class="legend-dot" style="background:#ef4444"></span> Reverted: {{ formatCU(block.compute.reverted) }}</div>
                        <div><span class="legend-dot" style="background:#374151"></span> Unused: {{ formatCU(48000000 - block.compute.total) }}</div>
                    </div>
                </div>

                <!-- DeFi Card -->
                <div class="analytics-hero-stat hero-stat--amber">
                    <div class="hero-label">DeFi Activity</div>
                    <div class="hero-value">{{ formatUSD(block.defi.swapVolume) }} swaps</div>
                    <div class="hero-breakdown mt-2 small">
                        <div>{{ formatUSD(block.defi.transferVolume) }} transfers</div>
                        <div>{{ block.defi.swapCount }} swaps · {{ block.defi.transferCount }} transfers</div>
                        <div>{{ block.defi.uniqueTraders }} traders · {{ block.defi.uniquePools }} pools</div>
                    </div>
                </div>
            </div>

            <!-- Leader & Performance Row -->
            <div class="row mb-4">
                <div class="col-md-6">
                    <div class="analytics-panel">
                        <div class="panel-header">
                            <div class="panel-title">Block Leader</div>
                        </div>
                        <div class="panel-body">
                            <div v-if="leader.identity">
                                <a v-if="leader.voteAccount" :href="'/validators/' + leader.voteAccount" class="text-gradient fs-5">
                                    {{ leader.name || formatPubkey(leader.identity) }}
                                </a>
                                <span v-else class="fs-5 font-mono">{{ formatPubkey(leader.identity) }}</span>
                                <div class="text-muted small mt-1 font-mono">{{ leader.identity }}</div>
                                <div v-if="leader.commission != null || leader.stake" class="mt-2 small">
                                    <span v-if="leader.commission != null"><span class="text-muted">Commission:</span> {{ leader.commission }}%</span>
                                    <span v-if="leader.stake" class="ms-3"><span class="text-muted">Stake:</span> {{ formatSOL(leader.stake / 1e9) }}</span>
                                </div>
                            </div>
                            <div v-else class="text-muted">Unknown validator</div>
                        </div>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="analytics-panel">
                        <div class="panel-header">
                            <div class="panel-title">Performance vs Network</div>
                        </div>
                        <div class="panel-body">
                            <div v-if="compareLoading" class="text-muted">
                                <span class="spinner-border spinner-border-sm me-2"></span>Loading comparison...
                            </div>
                            <div v-else-if="comparison" class="row">
                                <div class="col-4 text-center">
                                    <div class="small text-muted" title="Revenue rank among this validator's 4 consecutive slots">Leader's 4-Slot Group</div>
                                    <div class="fs-5 text-primary">#{{ comparison.neighbors.ranking.revenue.rank }} of {{ comparison.neighbors.ranking.revenue.of }}</div>
                                </div>
                                <div class="col-4 text-center">
                                    <div class="small text-muted" title="Revenue compared to hourly network median">vs Hourly Median</div>
                                    <div class="fs-5" :class="comparison.comparison.revenueVsMedian > 0 ? 'text-success' : 'text-danger'">
                                        {{ comparison.comparison.revenueVsMedian > 0 ? '+' : '' }}{{ comparison.comparison.revenueVsMedian.toFixed(1) }}%
                                    </div>
                                </div>
                                <div class="col-4 text-center">
                                    <div class="small text-muted" title="Revenue percentile vs all blocks in last hour">Hourly Percentile</div>
                                    <div class="fs-5" :class="comparison.outlier.percentile > 75 ? 'text-success' : comparison.outlier.percentile < 25 ? 'text-danger' : ''">
                                        {{ comparison.outlier.percentile }}th
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-muted">Comparison data unavailable</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Treemap Visualizations Row -->
            <div class="row mb-4">
                <div class="col-md-6">
                    <div class="analytics-panel h-100" :data-slot="slot">
                        <div class="panel-header">
                            <div class="panel-title">Compute Units Breakdown</div>
                        </div>
                        <div class="panel-body" style="height: 250px;">
                            <canvas id="cuTreemapCanvas"></canvas>
                        </div>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="analytics-panel h-100" :data-slot="slot">
                        <div class="panel-header">
                            <div class="panel-title">Fee Breakdown</div>
                        </div>
                        <div class="panel-body" style="height: 250px;">
                            <canvas id="feeTreemapCanvas"></canvas>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Detailed Metrics Grid -->
            <div class="row mb-4">
                <!-- Transaction Details -->
                <div class="col-md-4">
                    <div class="analytics-panel h-100">
                        <div class="panel-header">
                            <div class="panel-title">Transaction Metrics</div>
                        </div>
                        <div class="panel-body">
                            <table class="table table-sm table-borderless mb-0 metrics-table">
                                <tbody>
                                    <tr><td>Total Transactions</td><td class="text-end font-mono">{{ block.transactions.total.toLocaleString() }}</td></tr>
                                    <tr><td>Completed</td><td class="text-end font-mono text-success">{{ block.transactions.completed.toLocaleString() }}</td></tr>
                                    <tr><td>Reverted</td><td class="text-end font-mono text-danger">{{ block.transactions.reverted.toLocaleString() }}</td></tr>
                                    <tr><td>Vote Transactions</td><td class="text-end font-mono">{{ block.transactions.votes.toLocaleString() }}</td></tr>
                                    <tr><td>Non-Vote Transactions</td><td class="text-end font-mono">{{ block.transactions.nonVotes.toLocaleString() }}</td></tr>
                                    <tr><td>Completion Rate</td><td class="text-end font-mono">{{ block.transactions.completionRate.toFixed(2) }}%</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Compute Details -->
                <div class="col-md-4">
                    <div class="analytics-panel h-100">
                        <div class="panel-header">
                            <div class="panel-title">Compute Units</div>
                        </div>
                        <div class="panel-body">
                            <table class="table table-sm table-borderless mb-0 metrics-table">
                                <tbody>
                                    <tr><td>Total CU</td><td class="text-end font-mono">{{ block.compute.total.toLocaleString() }}</td></tr>
                                    <tr><td>Completed CU</td><td class="text-end font-mono text-success">{{ block.compute.completed.toLocaleString() }}</td></tr>
                                    <tr><td>Reverted CU</td><td class="text-end font-mono text-danger">{{ block.compute.reverted.toLocaleString() }}</td></tr>
                                    <tr><td>Utilization</td><td class="text-end font-mono">{{ block.compute.utilization.toFixed(2) }}%</td></tr>
                                    <tr><td>Avg CU/Tx</td><td class="text-end font-mono">{{ block.compute.avgPerTx.toLocaleString() }}</td></tr>
                                    <tr><td>Median CU/Tx</td><td class="text-end font-mono">{{ block.compute.medianPerTx.toLocaleString() }}</td></tr>
                                    <tr><td>Avg Fee/CU Limit (L)</td><td class="text-end font-mono">{{ block.compute.avgRatio.toFixed(2) }}</td></tr>
                                    <tr><td>Median Fee/CU Limit (L)</td><td class="text-end font-mono">{{ block.compute.medianRatio.toFixed(2) }}</td></tr>
                                    <tr><td>CU Diff (req vs used)</td><td class="text-end font-mono">{{ block.compute.diff.toLocaleString() }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Account Activity -->
                <div class="col-md-4">
                    <div class="analytics-panel h-100">
                        <div class="panel-header">
                            <div class="panel-title">Account Activity</div>
                        </div>
                        <div class="panel-body">
                            <table class="table table-sm table-borderless mb-0 metrics-table">
                                <tbody>
                                    <tr><td>Unique Accounts (block)</td><td class="text-end font-mono">{{ block.accounts.unique.toLocaleString() }}</td></tr>
                                    <tr><td>Unique Signers (block)</td><td class="text-end font-mono">{{ block.accounts.signers.toLocaleString() }}</td></tr>
                                    <tr><td>Unique Programs (block)</td><td class="text-end font-mono">{{ block.accounts.programs.toLocaleString() }}</td></tr>
                                    <tr><td>Accounts Created</td><td class="text-end font-mono text-success">+{{ block.accounts.created }}</td></tr>
                                    <tr><td>Accounts Closed</td><td class="text-end font-mono text-danger">-{{ block.accounts.closed }}</td></tr>
                                    <tr><td>Unique Writable (block)</td><td class="text-end font-mono">{{ block.accounts.writable.toLocaleString() }}</td></tr>
                                    <tr><td>Total Writable Refs</td><td class="text-end font-mono">{{ block.accounts.totalWritable.toLocaleString() }}</td></tr>
                                    <tr><td>Avg Accounts/Tx</td><td class="text-end font-mono">{{ block.accounts.avgAccounts.toFixed(1) }}</td></tr>
                                    <tr><td>Median Accounts/Tx</td><td class="text-end font-mono">{{ block.accounts.medianAccounts.toFixed(1) }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mb-4">
                <!-- Instructions -->
                <div class="col-md-4">
                    <div class="analytics-panel h-100">
                        <div class="panel-header">
                            <div class="panel-title">Instruction Complexity</div>
                        </div>
                        <div class="panel-body">
                            <table class="table table-sm table-borderless mb-0 metrics-table">
                                <tbody>
                                    <tr><td>Total Instructions</td><td class="text-end font-mono">{{ block.instructions.total.toLocaleString() }}</td></tr>
                                    <tr><td>Avg Instructions/Tx</td><td class="text-end font-mono">{{ block.instructions.avgPerTx.toFixed(2) }}</td></tr>
                                    <tr><td>Median Instructions/Tx</td><td class="text-end font-mono">{{ block.instructions.medianPerTx.toFixed(2) }}</td></tr>
                                    <tr><td>Inner Instructions</td><td class="text-end font-mono">{{ block.instructions.innerTotal.toLocaleString() }}</td></tr>
                                    <tr><td>Avg Inner/Tx</td><td class="text-end font-mono">{{ block.instructions.avgInner.toFixed(2) }}</td></tr>
                                    <tr><td>Median Inner/Tx</td><td class="text-end font-mono">{{ block.instructions.medianInner.toFixed(2) }}</td></tr>
                                    <tr><td>Avg CPI Depth</td><td class="text-end font-mono">{{ block.instructions.avgCpiDepth.toFixed(3) }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Jito MEV -->
                <div class="col-md-4">
                    <div class="analytics-panel h-100">
                        <div class="panel-header">
                            <div class="panel-title">Jito MEV</div>
                        </div>
                        <div class="panel-body">
                            <table class="table table-sm table-borderless mb-0 metrics-table">
                                <tbody>
                                    <tr><td>Total Tips</td><td class="text-end font-mono">{{ block.jito.total.toFixed(6) }} SOL</td></tr>
                                    <tr><td>Jito Transactions</td><td class="text-end font-mono">{{ block.jito.transactions.toLocaleString() }}</td></tr>
                                    <tr><td>Avg Tip</td><td class="text-end font-mono">{{ block.jito.avgTip.toFixed(6) }} SOL</td></tr>
                                    <tr><td>Median Tip</td><td class="text-end font-mono">{{ block.jito.medianTip.toFixed(6) }} SOL</td></tr>
                                    <tr><td>Dual-Tip Transactions</td><td class="text-end font-mono">{{ block.jito.dualTip.toLocaleString() }}</td></tr>
                                    <tr><td>Jito CU Used</td><td class="text-end font-mono">{{ block.jito.cu.toLocaleString() }}</td></tr>
                                    <tr><td>Tip per CU (L)</td><td class="text-end font-mono">{{ block.jito.cu > 0 ? ((block.jito.total * 1e9) / block.jito.cu).toFixed(2) : '—' }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Priority Fees -->
                <div class="col-md-4">
                    <div class="analytics-panel h-100">
                        <div class="panel-header">
                            <div class="panel-title">Priority Fees</div>
                        </div>
                        <div class="panel-body">
                            <table class="table table-sm table-borderless mb-0 metrics-table">
                                <tbody>
                                    <tr><td>Priority Transactions</td><td class="text-end font-mono">{{ block.priorityFees.transactions.toLocaleString() }}</td></tr>
                                    <tr><td>Avg Priority Rate (μL/CU)</td><td class="text-end font-mono">{{ block.priorityFees.average.toLocaleString() }}</td></tr>
                                    <tr><td>Median Priority Rate (μL/CU)</td><td class="text-end font-mono">{{ block.priorityFees.median.toLocaleString() }}</td></tr>
                                    <tr><td>Min Priority Rate (μL/CU)</td><td class="text-end font-mono">{{ block.priorityFees.min.toLocaleString() }}</td></tr>
                                    <tr><td>Max Priority Rate (μL/CU)</td><td class="text-end font-mono">{{ block.priorityFees.max.toLocaleString() }}</td></tr>
                                    <tr><td>Avg Total Fee/CU Used (L)</td><td class="text-end font-mono">{{ block.priorityFees.avgFeeRatio.toFixed(2) }}</td></tr>
                                    <tr><td>Median Total Fee/CU Used (L)</td><td class="text-end font-mono">{{ block.priorityFees.medianFeeRatio.toFixed(2) }}</td></tr>
                                    <tr><td>Fees/Volume (bps)</td><td class="text-end font-mono">{{ block.priorityFees.feesPerVolumeBps.toFixed(2) }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- DeFi & Transfers Row -->
            <div class="row">
                <div class="col-md-6">
                    <div class="analytics-panel">
                        <div class="panel-header">
                            <div class="panel-title">DeFi Swaps</div>
                        </div>
                        <div class="panel-body">
                            <table class="table table-sm table-borderless mb-0 metrics-table">
                                <tbody>
                                    <tr><td>Swap Volume (USD)</td><td class="text-end font-mono">{{ formatUSD(block.defi.swapVolume) }}</td></tr>
                                    <tr><td>Total Swaps</td><td class="text-end font-mono">{{ block.defi.swapCount.toLocaleString() }}</td></tr>
                                    <tr><td>Swap Transactions</td><td class="text-end font-mono">{{ block.defi.swapTxns.toLocaleString() }}</td></tr>
                                    <tr><td>Unique Pools</td><td class="text-end font-mono">{{ block.defi.uniquePools.toLocaleString() }}</td></tr>
                                    <tr><td>Unique Tokens</td><td class="text-end font-mono">{{ block.defi.uniqueTokens.toLocaleString() }}</td></tr>
                                    <tr><td>Unique Traders</td><td class="text-end font-mono">{{ block.defi.uniqueTraders.toLocaleString() }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="analytics-panel">
                        <div class="panel-header">
                            <div class="panel-title">Token Transfers</div>
                        </div>
                        <div class="panel-body">
                            <table class="table table-sm table-borderless mb-0 metrics-table">
                                <tbody>
                                    <tr><td>Transfer Volume (USD)</td><td class="text-end font-mono">{{ formatUSD(block.defi.transferVolume) }}</td></tr>
                                    <tr><td>Total Transfers</td><td class="text-end font-mono">{{ block.defi.transferCount.toLocaleString() }}</td></tr>
                                    <tr><td>Transfer Transactions</td><td class="text-end font-mono">{{ block.defi.transferTxns.toLocaleString() }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart, ArcElement, Tooltip, Legend, DoughnutController, LinearScale } from 'chart.js';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';

Chart.register(ArcElement, Tooltip, Legend, DoughnutController, LinearScale, TreemapController, TreemapElement);

export default {
    name: 'BlockAnalytics',
    props: {
        slot: { type: Number, required: true }
    },
    data() {
        return {
            loading: true,
            error: null,
            block: {
                slot: null,
                epoch: null,
                timestamp: null,
                rewards: 0,
                revenue: { total: 0, baseFees: 0, priorityFees: 0, jitoTips: 0 },
                transactions: { total: 0, completed: 0, reverted: 0, votes: 0, nonVotes: 0, completionRate: 0 },
                compute: { total: 0, completed: 0, reverted: 0, utilization: 0, avgPerTx: 0, medianPerTx: 0, avgRatio: 0, medianRatio: 0, diff: 0 },
                defi: { swapVolume: 0, swapCount: 0, swapTxns: 0, transferVolume: 0, transferCount: 0, transferTxns: 0, uniquePools: 0, uniqueTokens: 0, uniqueTraders: 0 },
                accounts: { unique: 0, signers: 0, programs: 0, created: 0, closed: 0, writable: 0, totalWritable: 0, avgAccounts: 0, medianAccounts: 0 },
                instructions: { total: 0, avgPerTx: 0, medianPerTx: 0, innerTotal: 0, avgInner: 0, medianInner: 0, avgCpiDepth: 0 },
                jito: { total: 0, avgTip: 0, medianTip: 0, transactions: 0, dualTip: 0, cu: 0 },
                priorityFees: { average: 0, median: 0, min: 0, max: 0, transactions: 0, avgFeeRatio: 0, medianFeeRatio: 0, feesPerVolumeBps: 0 }
            },
            leader: { identity: null, name: null, voteAccount: null, commission: null, stake: null },
            comparison: null,
            compareLoading: false,
            charts: {}
        };
    },
    mounted() {
        this.fetchBlockData();
    },
    beforeUnmount() {
        Object.values(this.charts).forEach(chart => {
            if (chart) chart.destroy();
        });
    },
    methods: {
        formatCU(value) {
            if (!value) return '0';
            if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
            if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
            if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
            return value.toLocaleString();
        },
        formatSOL(value) {
            if (!value) return '0 SOL';
            if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M SOL';
            if (value >= 1e3) return (value / 1e3).toFixed(2) + 'K SOL';
            return value.toFixed(2) + ' SOL';
        },
        formatUSD(value) {
            if (!value) return '$0.00';
            if (value >= 1e6) return '$' + (value / 1e6).toFixed(2) + 'M';
            if (value >= 1e3) return '$' + (value / 1e3).toFixed(2) + 'K';
            return '$' + value.toFixed(2);
        },
        formatPubkey(addr) {
            if (!addr) return 'Unknown';
            return addr.slice(0, 8) + '...' + addr.slice(-8);
        },
        formatTimestamp(ts) {
            if (!ts) return '—';
            return new Date(ts).toLocaleString();
        },
        async fetchBlockData() {
            this.loading = true;
            this.error = null;
            try {
                const res = await fetch('/analytics/api/block/' + this.slot);
                if (!res.ok) throw new Error(res.status === 404 ? 'Block not found' : 'Failed to load');
                const data = await res.json();
                Object.assign(this.block, data.block);
                Object.assign(this.leader, data.leader);
                this.$nextTick(() => this.initCharts());
                this.fetchComparisonData();
            } catch (e) {
                this.error = e.message;
            } finally {
                this.loading = false;
            }
        },
        async fetchComparisonData() {
            this.compareLoading = true;
            try {
                const res = await fetch('/analytics/api/block/' + this.slot + '/compare');
                if (res.ok) {
                    this.comparison = await res.json();
                }
            } catch (e) {
                console.error(e);
            } finally {
                this.compareLoading = false;
            }
        },
        initCharts() {
            this.initRevenueDonut();
            this.initTxDonut();
            this.initCuDonut();
            this.initCuTreemap();
            this.initFeeTreemap();
        },
        initRevenueDonut() {
            const el = document.getElementById('revenueDonut');
            if (!el) return;
            const ctx = el.getContext('2d');
            this.charts.revenue = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Base', 'Priority', 'Jito'],
                    datasets: [{
                        data: [this.block.revenue.baseFees, this.block.revenue.priorityFees, this.block.revenue.jitoTips],
                        backgroundColor: ['#6366f1', '#8b5cf6', '#f97316'],
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    cutout: '60%',
                    plugins: { legend: { display: false }, tooltip: { enabled: true } }
                }
            });
        },
        initTxDonut() {
            const el = document.getElementById('txDonut');
            if (!el) return;
            const ctx = el.getContext('2d');
            this.charts.tx = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Vote', 'Non-Vote', 'Reverted'],
                    datasets: [{
                        data: [this.block.transactions.votes, this.block.transactions.nonVotes - this.block.transactions.reverted, this.block.transactions.reverted],
                        backgroundColor: ['#3b82f6', '#8b5cf6', '#ef4444'],
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    cutout: '60%',
                    plugins: { legend: { display: false }, tooltip: { enabled: true } }
                }
            });
        },
        initCuDonut() {
            const el = document.getElementById('cuDonut');
            if (!el) return;
            const ctx = el.getContext('2d');
            const unused = 48000000 - this.block.compute.total;
            this.charts.cu = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Completed', 'Reverted', 'Unused'],
                    datasets: [{
                        data: [this.block.compute.completed, this.block.compute.reverted, unused > 0 ? unused : 0],
                        backgroundColor: ['#22c55e', '#ef4444', '#374151'],
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    cutout: '60%',
                    plugins: { legend: { display: false }, tooltip: { enabled: true } }
                }
            });
        },
        initCuTreemap() {
            const el = document.getElementById('cuTreemapCanvas');
            if (!el) return;
            const ctx = el.getContext('2d');

            const completed = this.block.compute.completed || 0;
            const reverted = this.block.compute.reverted || 0;
            const unused = Math.max(0, 48000000 - this.block.compute.total);

            const data = [
                { name: 'Completed CU', value: completed, category: 'completed' },
                { name: 'Reverted CU', value: reverted, category: 'reverted' },
                { name: 'Unused CU', value: unused, category: 'unused' }
            ].filter(d => d.value > 0);

            const colorMap = { completed: '#22c55e', reverted: '#ef4444', unused: '#374151' };

            this.charts.cuTreemap = new Chart(ctx, {
                type: 'treemap',
                data: {
                    datasets: [{
                        tree: data,
                        key: 'value',
                        groups: ['name'],
                        spacing: 2,
                        borderWidth: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        backgroundColor: (ctx) => {
                            const item = ctx.dataset.data[ctx.dataIndex];
                            if (!item) return '#374151';
                            const orig = data.find(d => d.name === item.g);
                            return orig ? colorMap[orig.category] : '#374151';
                        },
                        labels: {
                            display: true,
                            align: 'center',
                            position: 'middle',
                            color: '#fff',
                            font: { size: 12, family: "'JetBrains Mono', monospace" },
                            formatter: (ctx) => {
                                const item = ctx.dataset.data[ctx.dataIndex];
                                if (!item || !item.g) return '';
                                const pct = ((item.v / 48000000) * 100).toFixed(1);
                                return [item.g.replace(' CU', ''), pct + '%'];
                            }
                        }
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: () => '',
                                label: (ctx) => {
                                    const item = ctx.raw;
                                    if (!item) return '';
                                    return item.g + ': ' + item.v.toLocaleString() + ' CU (' + ((item.v / 48000000) * 100).toFixed(1) + '%)';
                                }
                            }
                        }
                    }
                }
            });
        },
        initFeeTreemap() {
            const el = document.getElementById('feeTreemapCanvas');
            if (!el) return;
            const ctx = el.getContext('2d');

            const base = this.block.revenue.baseFees || 0;
            const priority = this.block.revenue.priorityFees || 0;
            const jito = this.block.revenue.jitoTips || 0;
            const total = base + priority + jito;

            if (total === 0) return;

            const data = [
                { name: 'Base Fees', value: base, category: 'base' },
                { name: 'Priority Fees', value: priority, category: 'priority' },
                { name: 'Jito Tips', value: jito, category: 'jito' }
            ].filter(d => d.value > 0);

            const colorMap = { base: '#6366f1', priority: '#8b5cf6', jito: '#f97316' };

            this.charts.feeTreemap = new Chart(ctx, {
                type: 'treemap',
                data: {
                    datasets: [{
                        tree: data,
                        key: 'value',
                        groups: ['name'],
                        spacing: 2,
                        borderWidth: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        backgroundColor: (ctx) => {
                            const item = ctx.dataset.data[ctx.dataIndex];
                            if (!item) return '#6366f1';
                            const orig = data.find(d => d.name === item.g);
                            return orig ? colorMap[orig.category] : '#6366f1';
                        },
                        labels: {
                            display: true,
                            align: 'center',
                            position: 'middle',
                            color: '#fff',
                            font: { size: 12, family: "'JetBrains Mono', monospace" },
                            formatter: (ctx) => {
                                const item = ctx.dataset.data[ctx.dataIndex];
                                if (!item || !item.g) return '';
                                const pct = ((item.v / total) * 100).toFixed(1);
                                const solVal = item.v.toPrecision(3);
                                return [item.g, solVal + ' SOL', pct + '%'];
                            }
                        }
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: () => '',
                                label: (ctx) => {
                                    const item = ctx.raw;
                                    if (!item) return '';
                                    return item.g + ': ' + item.v.toFixed(6) + ' SOL (' + ((item.v / total) * 100).toFixed(1) + '%)';
                                }
                            }
                        }
                    }
                }
            });
        }
    }
};
</script>

<style scoped>
.hero-chart-container {
    display: flex;
    justify-content: center;
    margin: 0.5rem 0;
}
.hero-chart-container canvas {
    max-width: 80px;
    max-height: 80px;
}
.hero-breakdown {
    font-size: 0.75rem;
    line-height: 1.5;
}
.hero-breakdown > div {
    display: flex;
    align-items: center;
    white-space: nowrap;
}
.legend-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    min-width: 10px;
    border-radius: 50%;
    margin-right: 6px;
    flex-shrink: 0;
}
.metrics-table td:first-child {
    color: #9ca3af;
}
.metrics-table td {
    padding: 0.35rem 0;
}
.font-mono {
    font-family: 'JetBrains Mono', monospace;
}
</style>
